/**
 * The tab "Learnings" of the page "Learn from sales" (lead decision S7, tab 4), and the
 * parts of a learning that the drawer of one inquiry shows too: the status badge, the
 * evidence (case, quotation, sales order, sales person) and "Approve" / "Retract".
 *
 * A learning is what I2Q learns, or confirms, from a quotation sales made in F&O
 * (`rfq_truth_learning`, written by the TRUTH LEARN stage). "Approve" and "Retract" need
 * the capability `approve_learned` (managers); without it the row says "A manager
 * approves." "Approve" shows only while the switch `truth_learn_apply` is ON (the answer
 * says so in `apply_on`), and never on a learning the server always refuses (observed
 * only, an item without a customer account). The server stays the gate: it answers 403
 * without the capability, 404 out of the areas of the session and 409 when the learning
 * changed or the switch is OFF. A decision reads every list of the page again.
 *
 * Decision D5 (20 Oct 2026): a customer learning whose sender buys for more than one
 * customer account shows the badge "Several accounts"; "Approve" is not offered for it (the
 * server answers 409). A manager clears the mark ("Clear the mark") or sets it ("Mark:
 * several accounts"); the mark acts on new drafts only while the switch
 * `truth_learn_multi_account` is ON.
 *
 * Round 3 (20 Oct 2026, goal A): an item learning whose learned item is on the sales order
 * of its quotation shows the tag "Ordered" beside what sales quoted (`line_ordered`).
 */

import { Badge, Button, DataGrid, type DataGridColumnDef, Typography } from "@norican/react";
import { Link } from "react-router";

import { ColumnCards } from "@/components/column-cards";
import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import {
  useApproveTruthLearning,
  useRetractTruthLearning,
  useSetTruthLearningMultiAccount,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import {
  CELL_LABELS,
  LEARNING_LABELS,
  MULTI_ACCOUNT_LABELS,
  multiAccountSendersText,
  OBSERVE_REASON_LABELS,
  ORDER_LINE_LABELS,
} from "./labels";
import type { Learning, LearningList } from "./normalize";
import {
  canApprove,
  canMark,
  canRetract,
  learningFailureText,
  learningKindLabel,
  learningStatusHint,
  learningStatusLabel,
  learningStatusTone,
  personText,
  statusCounts,
} from "./view";

/** The status of a learning, with what it means for its kind as its title. */
export function LearningStatusBadge({
  status,
  kind,
  note,
}: {
  status: string;
  kind: string;
  note?: string | null;
}) {
  return (
    <Badge
      tone={learningStatusTone(status)}
      variant="subtle"
      size="sm"
      title={learningStatusHint(status, kind, note)}
      data-learning-status={status}
    >
      {learningStatusLabel(status)}
    </Badge>
  );
}

/** The badge of a sender with the multi-account mark (decision D5). */
export function MultiAccountBadge() {
  return (
    <Badge
      tone="warning"
      variant="outline"
      size="sm"
      title={MULTI_ACCOUNT_LABELS.hint}
      data-learning-multi-account=""
    >
      {MULTI_ACCOUNT_LABELS.badge}
    </Badge>
  );
}

/**
 * "Approve" and "Retract" for a manager, and for a customer learning the mark of the
 * sender ("Clear the mark" / "Mark: several accounts"); "A manager approves." for everyone
 * else.
 */
export function LearningActions({
  learning,
  canDecide,
  applyOn,
}: {
  learning: Learning;
  canDecide: boolean;
  /** The switch `truth_learn_apply` is ON. */
  applyOn: boolean;
}) {
  const approve = useApproveTruthLearning();
  const retract = useRetractTruthLearning();
  const mark = useSetTruthLearningMultiAccount();
  const approvable = canApprove(learning, applyOn);
  const retractable = canRetract(learning.status);
  const markable = canDecide && canMark(learning);
  if (!approvable && !retractable && !markable) {
    return null;
  }
  if (!canDecide) {
    return (
      <Typography as="span" variant="caption" tone="muted" data-learning-by-manager="">
        {LEARNING_LABELS.byManager}
      </Typography>
    );
  }
  const busy = approve.isPending || retract.isPending || mark.isPending;
  const error = approve.error ?? retract.error ?? mark.error;
  const marked = learning.sender_multi_account === true;
  return (
    <span
      className="flex min-w-[12rem] flex-col gap-1 whitespace-normal"
      data-learning-actions={learning.learning_id}
    >
      <span className="flex flex-wrap gap-1">
        {approvable && (
          <Button
            size="sm"
            variant="outline"
            tone="accent"
            loading={approve.isPending}
            disabled={busy}
            onClick={() => {
              logger.debug("[learn-from-sales] approve", learning.learning_id);
              retract.reset();
              approve.mutate(learning.learning_id);
            }}
            data-learning-approve=""
          >
            {LEARNING_LABELS.approve}
          </Button>
        )}
        {retractable && (
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            loading={retract.isPending}
            disabled={busy}
            onClick={() => {
              logger.debug("[learn-from-sales] retract", learning.learning_id);
              approve.reset();
              retract.mutate(learning.learning_id);
            }}
            data-learning-retract=""
          >
            {LEARNING_LABELS.retract}
          </Button>
        )}
        {markable && (
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            loading={mark.isPending}
            disabled={busy}
            onClick={() => {
              logger.debug("[learn-from-sales] multi-account", learning.learning_id, !marked);
              approve.reset();
              retract.reset();
              mark.mutate({ learningId: learning.learning_id, marked: !marked });
            }}
            data-learning-mark={marked ? "clear" : "set"}
          >
            {marked ? MULTI_ACCOUNT_LABELS.clear : MULTI_ACCOUNT_LABELS.mark}
          </Button>
        )}
      </span>
      {error && (
        <Typography as="span" variant="caption" className="text-danger-fg break-words" role="alert">
          {learningFailureText(error)}
        </Typography>
      )}
    </span>
  );
}

/**
 * The inquiry line (an item) or the sender I2Q learns the customer for (a customer, with
 * the account as a caption), with its notes.
 */
function LearningText({ learning }: { learning: Learning }) {
  const customer = learning.kind === "customer";
  const text = customer
    ? learning.inquiry_text?.trim() || CELL_LABELS.notKnown
    : learning.inquiry_text?.trim() || CELL_LABELS.noText;
  const account = learning.customer_account ?? learning.sales_value;
  const reason = learning.observe_reason ? OBSERVE_REASON_LABELS[learning.observe_reason] : null;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="break-words" data-learning-text={learning.kind}>
        {text}
      </span>
      {customer && learning.sender_multi_account === true && (
        <span>
          <MultiAccountBadge />
        </span>
      )}
      {customer && account && (
        <Typography as="span" variant="caption" tone="muted" className="break-words">
          {`${LEARNING_LABELS.account} ${account}`}
        </Typography>
      )}
      {learning.observe_only && reason && (
        <Typography as="span" variant="caption" tone="muted" className="break-words">
          {reason}
        </Typography>
      )}
      {learning.note && (
        <Typography as="span" variant="caption" tone="muted" className="break-words">
          {learning.note}
        </Typography>
      )}
    </span>
  );
}

/** Where the learning comes from: the case, the quotation, the sales order, the person. */
export function LearningEvidence({
  learning,
  withCase = true,
}: {
  learning: Learning;
  withCase?: boolean;
}) {
  return (
    <span className="flex flex-col gap-0.5 text-sm">
      {withCase && (
        <Link
          to={`/case/${encodeURIComponent(learning.case_id)}`}
          className="text-accent-fg-subtle break-all hover:underline"
        >
          {`${LEARNING_LABELS.caseLink} ${learning.case_id}`}
        </Link>
      )}
      <span className="break-words">
        {[learning.quotation, learning.sales_order].filter(Boolean).join(", ") ||
          CELL_LABELS.notKnown}
      </span>
      <Typography as="span" variant="caption" tone="muted" data-learning-person="">
        {personText(learning)}
      </Typography>
    </span>
  );
}

/** The columns of a learning; the drawer leaves out the case. */
export function learningColumns(options: {
  canDecide: boolean;
  applyOn: boolean;
  withCase?: boolean;
}): DataGridColumnDef<Learning>[] {
  const withCase = options.withCase ?? true;
  return [
    {
      id: "kind",
      header: LEARNING_LABELS.kind,
      cell: (row) => learningKindLabel(row.kind),
    },
    {
      id: "text",
      header: LEARNING_LABELS.text,
      cell: (row) => <LearningText learning={row} />,
    },
    {
      id: "i2q",
      header: LEARNING_LABELS.i2q,
      cell: (row) => <span className="break-words">{row.i2q_value ?? CELL_LABELS.none}</span>,
    },
    {
      id: "sales",
      header: LEARNING_LABELS.sales,
      cell: (row) => (
        <span className="flex flex-wrap items-center gap-1">
          <span className="break-words">{row.sales_value ?? CELL_LABELS.none}</span>
          {row.line_ordered === true && (
            <Badge
              tone="info"
              variant="outline"
              size="sm"
              title={ORDER_LINE_LABELS.learningHint}
              data-learning-ordered=""
            >
              {ORDER_LINE_LABELS.ordered}
            </Badge>
          )}
        </span>
      ),
    },
    {
      id: "status",
      header: LEARNING_LABELS.status,
      cell: (row) => <LearningStatusBadge status={row.status} kind={row.kind} note={row.note} />,
    },
    {
      id: "evidence",
      header: LEARNING_LABELS.evidence,
      cell: (row) => <LearningEvidence learning={row} withCase={withCase} />,
    },
    {
      id: "hits",
      header: LEARNING_LABELS.hits,
      align: "end",
      cell: (row) =>
        row.status === "active" ? (
          <span className="tabular-nums" title={LEARNING_LABELS.hitsAfter}>
            {row.hits}
          </span>
        ) : (
          ""
        ),
    },
    {
      id: "decide",
      header: "",
      cell: (row) => (
        <LearningActions learning={row} canDecide={options.canDecide} applyOn={options.applyOn} />
      ),
    },
  ];
}

/** The learnings as a table on a wide screen, as cards on a phone and in the drawer. */
export function LearningTable({
  rows,
  canDecide,
  applyOn,
  withCase = true,
  cards = false,
}: {
  rows: readonly Learning[];
  canDecide: boolean;
  /** The switch `truth_learn_apply` is ON (`apply_on` of the answer). */
  applyOn: boolean;
  withCase?: boolean;
  /** Always cards (the drawer is narrow). */
  cards?: boolean;
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const columns = learningColumns({ canDecide, applyOn, withCase });
  const getRowId = (row: Learning) => String(row.learning_id);
  if (narrow || cards) {
    return (
      <ColumnCards
        columns={columns}
        data={rows}
        getRowId={getRowId}
        lead={["text"]}
        label={LEARNING_LABELS.table}
      />
    );
  }
  return (
    <div className="min-w-0 overflow-x-auto">
      <DataGrid<Learning>
        columns={columns}
        data={[...rows]}
        getRowId={getRowId}
        density="compact"
        aria-label={LEARNING_LABELS.table}
        className="min-w-[960px]"
      />
    </div>
  );
}

/** The counts of the list by status, as buttons: a click filters on the status. */
export function LearningCounts({
  list,
  picked,
  onPick,
}: {
  list: LearningList;
  picked: string | undefined;
  onPick: (status: Learning["status"] | undefined) => void;
}) {
  const entries = statusCounts(list.counts, picked as Learning["status"] | undefined);
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-2" data-learning-counts="">
      {entries.map((status) => (
        <Button
          key={status}
          size="sm"
          variant={status === picked ? "subtle" : "outline"}
          tone={status === picked ? "accent" : "neutral"}
          aria-pressed={status === picked}
          onClick={() => onPick(status === picked ? undefined : status)}
          data-learning-count={status}
        >
          <span className="tabular-nums">{`${learningStatusLabel(status)} ${list.counts[status] ?? 0}`}</span>
        </Button>
      ))}
    </div>
  );
}

/**
 * Decision D5: how many senders of the list buy for several accounts, and, for a manager,
 * that the drafts use the mark only while the switch `truth_learn_multi_account` is ON.
 */
export function MultiAccountNote({
  senders,
  switchOn,
  canDecide,
  hasCustomers,
}: {
  /** `counts.multi_account_senders` of the answer. */
  senders: number;
  /** `multi_account_on` of the answer. */
  switchOn: boolean;
  canDecide: boolean;
  /** The list shows at least one customer learning. */
  hasCustomers: boolean;
}) {
  const offNote = canDecide && !switchOn && (senders > 0 || hasCustomers);
  if (senders <= 0 && !offNote) {
    return null;
  }
  return (
    <span className="flex flex-col gap-0.5" data-lfs-multi-account="">
      {senders > 0 && (
        <Typography as="span" variant="caption" tone="muted" data-lfs-multi-senders={senders}>
          {multiAccountSendersText(senders)}
        </Typography>
      )}
      {offNote && (
        <Typography as="span" variant="caption" tone="muted" data-lfs-multi-off="">
          {MULTI_ACCOUNT_LABELS.off}
        </Typography>
      )}
    </span>
  );
}
